import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { initErrorReporter } from '@/lib/errorReporter'
import { initSentry, captureAnalyticsEvent } from '@/lib/sentryInit'
import { subscribe } from '@/lib/analytics'
import ErrorBoundary from '@/components/ErrorBoundary'
import App from '@/App.jsx'
import '@/index.css'
import '@/styles/liquid.css'
import './i18n'

// Initialize global error capture before anything else. The errorReporter
// exports `registerExternalReporter(fn)` so an observability SDK (Sentry)
// can be plugged in with a single call from here — no other call sites
// need changes.
initErrorReporter()

// Phase 4 (audit-2026-04-27) — Sentry SDK loads only when VITE_SENTRY_DSN
// is configured. Fire-and-forget: failures here NEVER block app render.
//
// El listener del bus de analítica se engancha DESPUÉS de que resuelva, no en
// paralelo: `initSentry` es async y carga la SDK con un `await import()`, así que
// un `subscribe` suelto aquí recibiría los primeros eventos con la SDK todavía a
// null y los perdería en silencio.
//
// Powens R135 pide "user flow data ... with all associated conversion rates":
// Sentry es el destino PROVISIONAL — ya está configurado y su checklist lo cita
// como ejemplo válido. El día que se elija un proveedor de producto, se cambia
// este único enganche y ni un solo `track()` se entera. Para eso está el bus.
initSentry()
  .then(() => {
    subscribe(({ name, ts, ...props }) => captureAnalyticsEvent(name, { ...props, ts }))
  })
  .catch(() => {})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,       // 2 min — data considered fresh
      gcTime: 10 * 60 * 1000,          // 10 min — keep inactive cache before GC
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary title="Error crítico" message="La aplicación ha encontrado un error inesperado. Por favor, recarga la página.">
      <ThemeProvider attribute="class" defaultTheme="light" storageKey="verix_theme">
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
)
